import * as RadixTooltip from '@radix-ui/react-tooltip';
import React, { useEffect, useRef, type ReactNode } from 'react';
import { Link } from 'react-router';

interface TooltipLink {
  text: string;
  href: string;
}

interface Props {
  content: string;
  children: ReactNode;
  link?: TooltipLink;
  isOpen?: boolean;
  isTextLeft?: boolean;
  maxWidth?: number;
  side?: 'top' | 'right' | 'bottom' | 'left';
  onOpenChange?: (open: boolean) => void;
}

export const Tooltip: React.FC<Props> = ({
  content,
  children,
  link,
  isOpen,
  isTextLeft = false,
  side = 'bottom',
  maxWidth = 480,
  onOpenChange,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const close = () => onOpenChange?.(false);

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (contentRef.current?.contains(target)) {
        return;
      }

      const triggers = Array.from(
        document.querySelectorAll('[data-tooltip-trigger]'),
      );
      const isInsideTrigger = triggers.some((trigger) =>
        trigger.contains(target),
      );

      if (isInsideTrigger) {
        return;
      }

      close();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    const handleScroll = () => close();

    const timeoutId = window.setTimeout(() => {
      document.addEventListener('mousedown', handlePointerDown);
      document.addEventListener('keydown', handleKeyDown);
      window.addEventListener('scroll', handleScroll, true);
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, onOpenChange]);

  return (
    <RadixTooltip.Provider delayDuration={99999} disableHoverableContent>
      <RadixTooltip.Root open={isOpen} onOpenChange={() => undefined}>
        <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            ref={contentRef}
            side={side}
            sideOffset={8}
            align="center"
            collisionPadding={16}
            style={{
              maxWidth: `min(${maxWidth}px, var(--radix-popper-available-width))`,
            }}
            className={`z-50 flex flex-col gap-1 rounded-xl bg-constant-black p-3 text-caption-12 font-medium text-white shadow-lg contrast:bg-white contrast:text-constant-black ${isTextLeft ? 'text-left' : 'text-center'}`}
          >
            <p className="whitespace-pre-line">{content}</p>
            {link && (
              <Link
                to={link.href}
                className="w-fit py-1 text-subhead-14 text-primary"
              >
                {link.text}
              </Link>
            )}
            <RadixTooltip.Arrow
              width={12}
              height={6}
              className="-mt-px fill-constant-black contrast:fill-white"
            />
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
};
